import {
  PortalSigninLinkFailureSchema,
  type PortalSigninLinkFailure,
} from "@ledgerbox/domain"

/* What the sign-in panel shows, as data. The panel only draws a step; which
   step comes first after a link from the email fails is decided here. */

/** Who a code goes to: an address the client typed, or the contact behind
    a used link, whose address the page only knows masked. */
export type SigninIdentity =
  | { kind: "email"; email: string }
  | { kind: "link"; signinToken: string; maskedEmail: string }

export type SigninStep =
  | { kind: "email"; notice: string | null }
  | { kind: "expired"; signinToken: string; maskedEmail: string }
  | { kind: "reissued"; maskedEmail: string }
  | { kind: "used"; signinToken: string; maskedEmail: string }
  | { kind: "code"; challengeId: string; identity: SigninIdentity }

export const invalidLinkNotice =
  "This link does not sign you in. Enter your email to get a code."

/** The reason a link was refused, when the error is one. */
export const signinLinkFailureOf = (
  details: unknown
): PortalSigninLinkFailure | null => {
  const parsed = PortalSigninLinkFailureSchema.safeParse({ error: details })
  return parsed.success ? parsed.data.error : null
}

/**
 * The first step after a link from the email did not sign the client in.
 * Expired offers a new link, used offers a code (a scanner that used the
 * first link would use a reissued one too), and anything else is the plain
 * email step with a line that says why.
 */
export const firstSigninStep = (
  failure: PortalSigninLinkFailure | null,
  signinToken: string | null
): SigninStep => {
  if (!failure || !signinToken) return { kind: "email", notice: null }
  if (failure.maskedEmail && failure.reason === "expired") {
    return { kind: "expired", signinToken, maskedEmail: failure.maskedEmail }
  }
  if (failure.maskedEmail && failure.reason === "used") {
    return { kind: "used", signinToken, maskedEmail: failure.maskedEmail }
  }
  return { kind: "email", notice: invalidLinkNotice }
}

/** The line at the foot of the panel: the proof it asks for, in one line. */
export const authNote = (step: SigninStep): string =>
  step.kind === "expired" || step.kind === "reissued"
    ? "No account, no password. The link in your email is how we know it is you."
    : "No account, no password. The code is how we know it is you."

/** The address the code step names. */
export const shownAddress = (identity: SigninIdentity): string =>
  identity.kind === "email" ? identity.email : identity.maskedEmail

/** "Signed in as" names the person, or their address when the firm gave no
    name. */
export const signedInName = (contact: {
  name: string | null
  email: string
}): string => contact.name?.trim() || contact.email
